import React, { useState, useEffect } from 'react';
import { 
  Grid, Typography, Box, Chip, 
  Divider, Alert, CircularProgress,
  Card, CardContent, CardActionArea,
  LinearProgress, Collapse, Button,
  List, ListItem, ListItemIcon, ListItemText,
  IconButton, FormControl, InputLabel, Select,
  MenuItem, Stack, Tooltip, TextField
} from '@mui/material';
import GPUCard from '../components/GPUCard';
import apiService from '../services/api';
import RefreshIcon from '@mui/icons-material/Refresh';
import DnsIcon from '@mui/icons-material/Dns';
import GroupIcon from '@mui/icons-material/Group';
import StorageIcon from '@mui/icons-material/Storage';
import MemoryIcon from '@mui/icons-material/Memory';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const [nodesData, setNodesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [filterOptions, setFilterOptions] = useState({
    users: [],
    clusters: [],
    versions: []
  });
  
  // Filter and sort state
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedCluster, setSelectedCluster] = useState('');
  const [selectedVersion, setSelectedVersion] = useState('');
  const [sortBy, setSortBy] = useState('node_id');
  const [showFilters, setShowFilters] = useState(false);
  
  const navigate = useNavigate();

  const fetchFilterOptions = async () => {
    try {
      const options = await apiService.getFilterOptions();
      setFilterOptions(options);
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  const fetchNodesData = async () => {
    setLoading(true);
    try {
      const filters = {
        user: selectedUser || undefined,
        cluster: selectedCluster || undefined,
        version: selectedVersion || undefined,
        sortBy: sortBy || 'node_id'
      };
      
      const data = await apiService.getNodes(filters);
      setNodesData(data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error('Error fetching node stats:', err);
      setError('Failed to fetch node statistics. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
    fetchNodesData();
    
    // Set up polling for real-time updates
    const interval = setInterval(() => {
      fetchNodesData();
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(interval);
  }, []);
  
  // Refetch data when filters change
  useEffect(() => {
    fetchNodesData();
  }, [selectedUser, selectedCluster, selectedVersion, sortBy]);

  const toggleNodeExpand = (nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };

  const handleNodeClick = (nodeId) => {
    navigate(`/node/${nodeId}`);
  };
  
  // Handle filter and sort changes
  const handleUserChange = (event) => {
    setSelectedUser(event.target.value);
  };
  
  const handleClusterChange = (event) => {
    setSelectedCluster(event.target.value);
  };
  
  const handleVersionChange = (event) => {
    setSelectedVersion(event.target.value);
  };
  
  const handleSortChange = (event) => {
    setSortBy(event.target.value);
  };
  
  const handleClearFilters = () => {
    setSelectedUser('');
    setSelectedCluster('');
    setSelectedVersion('');
    setSortBy('node_id');
  };

  // Count total GPUs across all nodes
  const totalGpus = nodesData.reduce((count, node) => count + node.gpu_count, 0);
  
  // Calculate overall GPU utilization
  const calculcateOverallUtilization = () => {
    let totalUtil = 0;
    let totalGpus = 0;
    
    nodesData.forEach(node => {
      totalUtil += node.avg_gpu_utilization * node.gpu_count;
      totalGpus += node.gpu_count;
    });
    
    return totalGpus > 0 ? Math.round(totalUtil / totalGpus) : 0;
  };
  
  // Get total active users (unique)
  const getTotalActiveUsers = () => {
    const uniqueUsers = new Set();
    nodesData.forEach(node => {
      if (Array.isArray(node.active_users)) {
        node.active_users.forEach(user => uniqueUsers.add(user));
      }
    });
    return uniqueUsers.size;
  };
  
  const renderFilterSection = () => {
    if (!showFilters) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button 
            startIcon={<FilterListIcon />} 
            onClick={() => setShowFilters(true)}
            variant="outlined"
            size="small"
          >
            Show Filters
          </Button>
        </Box>
      );
    }
    
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Filters & Sorting</Typography>
            <IconButton size="small" onClick={() => setShowFilters(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="user-filter-label">Filter by User</InputLabel>
                <Select
                  labelId="user-filter-label"
                  value={selectedUser}
                  label="Filter by User"
                  onChange={handleUserChange}
                >
                  <MenuItem value="">
                    <em>All Users</em>
                  </MenuItem>
                  {filterOptions.users.map((user) => (
                    <MenuItem key={user} value={user}>{user}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="cluster-filter-label">Filter by Cluster</InputLabel>
                <Select
                  labelId="cluster-filter-label"
                  value={selectedCluster}
                  label="Filter by Cluster"
                  onChange={handleClusterChange}
                >
                  <MenuItem value="">
                    <em>All Clusters</em>
                  </MenuItem>
                  {filterOptions.clusters.map((cluster) => (
                    <MenuItem key={cluster} value={cluster}>{cluster}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="version-filter-label">Filter by Version</InputLabel>
                <Select
                  labelId="version-filter-label"
                  value={selectedVersion}
                  label="Filter by Version"
                  onChange={handleVersionChange}
                >
                  <MenuItem value="">
                    <em>All Versions</em>
                  </MenuItem>
                  {filterOptions.versions.map((version) => (
                    <MenuItem key={version} value={version}>{version}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="sort-by-label">Sort By</InputLabel>
                <Select
                  labelId="sort-by-label"
                  value={sortBy}
                  label="Sort By"
                  onChange={handleSortChange}
                >
                  <MenuItem value="node_id">Node ID</MenuItem>
                  <MenuItem value="last_activity">Least Recently Used</MenuItem>
                  <MenuItem value="active_users">Number of Active Users</MenuItem>
                  <MenuItem value="version">Version</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleClearFilters}
              sx={{ mr: 1 }}
            >
              Clear Filters
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              size="small" 
              onClick={fetchNodesData}
            >
              Apply Filters
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderSummaryCards = () => {
    const totalNodes = nodesData.length;
    const avgGpuUtil = calculcateOverallUtilization();
    const totalActiveUsers = getTotalActiveUsers();
    
    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <DnsIcon sx={{ color: 'primary.main', mr: 1 }} />
                <Typography variant="h6" component="div">
                  Nodes
                </Typography>
              </Box>
              <Typography variant="h3" component="div" sx={{ mb: 1 }}>
                {totalNodes}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total compute nodes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <StorageIcon sx={{ color: 'success.main', mr: 1 }} />
                <Typography variant="h6" component="div">
                  GPUs
                </Typography>
              </Box>
              <Typography variant="h3" component="div" sx={{ mb: 1 }}>
                {totalGpus}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total available GPUs
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <MemoryIcon sx={{ color: 'warning.main', mr: 1 }} />
                <Typography variant="h6" component="div">
                  Utilization
                </Typography>
              </Box>
              <Typography variant="h3" component="div" sx={{ mb: 1 }}>
                {avgGpuUtil}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average GPU utilization
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <GroupIcon sx={{ color: 'info.main', mr: 1 }} />
                <Typography variant="h6" component="div">
                  Users
                </Typography>
              </Box>
              <Typography variant="h3" component="div" sx={{ mb: 1 }}>
                {totalActiveUsers}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active unique users
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const getUtilizationColor = (utilization) => {
    if (utilization < 30) return 'success';
    if (utilization < 70) return 'warning';
    return 'error';
  };
  
  const isNodeStale = (node) => {
    if (!node.timestamp) return false;
    
    const lastUpdateTime = new Date(node.timestamp);
    const currentTime = new Date();
    const diffSeconds = (currentTime - lastUpdateTime) / 1000;
    
    // Consider stale if > 60 seconds since last update
    return diffSeconds > 60;
  };
  
  const getStaleNodeStyling = (node) => {
    if (!isNodeStale(node)) return {};
    
    // Return styling for stale nodes
    return {
      opacity: 0.8,
      border: '1px solid #ff9800',
      backgroundColor: 'rgba(255, 152, 0, 0.05)'
    };
  };

  const renderNodeCard = (node) => {
    const isExpanded = expandedNodes[node.node_id] || false;
    
    return (
      <Card 
        key={node.node_id} 
        sx={{ 
          mb: 2, 
          transition: 'all 0.2s',
          ':hover': {
            boxShadow: 3,
          },
          ...getStaleNodeStyling(node)
        }}
      >
        <CardActionArea onClick={() => handleNodeClick(node.node_id)}>
          <CardContent sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" component="div">
                {node.node_id.split('.')[0]}
              </Typography>
              <Box>
                <Chip 
                  label={`${node.gpu_count} GPUs`} 
                  color="primary" 
                  size="small" 
                  sx={{ mr: 1 }} 
                />
                
                {sortBy === 'last_activity' && node.last_activity_time && (
                  <Tooltip title="Last Active">
                    <Chip
                      icon={<AccessTimeIcon />}
                      label={new Date(node.last_activity_time).toLocaleDateString() === new Date().toLocaleDateString() 
                        ? new Date(node.last_activity_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) 
                        : new Date(node.last_activity_time).toLocaleString()}
                      size="small"
                      variant="outlined"
                    />
                  </Tooltip>
                )}
                
                {node.monitor_version && sortBy === 'version' && (
                  <Chip 
                    label={`v${node.monitor_version}`} 
                    variant="outlined" 
                    size="small" 
                  />
                )}
                
                {node.cluster_name && (
                  <Chip 
                    label={node.cluster_name} 
                    variant="outlined" 
                    size="small"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
            </Box>
            
            {isNodeStale(node) && (
              <Alert 
                severity="warning" 
                sx={{ 
                  mt: 1, 
                  py: 0,
                  alignItems: 'center',
                  '& .MuiAlert-message': { padding: '2px 0' } 
                }}
              >
                No response since {new Date(node.timestamp).toLocaleTimeString()}
              </Alert>
            )}
            
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                GPU Utilization: {Math.round(node.avg_gpu_utilization)}%
              </Typography>
              <Tooltip title={`${Math.round(node.avg_gpu_utilization)}% GPU Utilization`}>
                <LinearProgress 
                  variant="determinate" 
                  value={node.avg_gpu_utilization} 
                  color={getUtilizationColor(node.avg_gpu_utilization)}
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Tooltip>
            </Box>
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                {Array.isArray(node.active_users) && node.active_users.length > 0 ? (
                  <Tooltip title={`Active Users: ${node.active_users.join(', ')}`}>
                    <Chip 
                      icon={<GroupIcon />} 
                      label={`${node.active_users.length} Active Users`} 
                      size="small" 
                      color={node.active_users.length > 0 ? "info" : "default"}
                      variant="outlined"
                    />
                  </Tooltip>
                ) : (
                  <Chip 
                    icon={<GroupIcon />} 
                    label="No Active Users" 
                    size="small" 
                    color="default"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          PikaGPU Dashboard
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {loading ? (
            <CircularProgress size={24} sx={{ mr: 2 }} />
          ) : (
            <Button 
              variant="outlined" 
              startIcon={<RefreshIcon />} 
              onClick={fetchNodesData}
              sx={{ mr: 2 }}
              size="small"
            >
              Refresh
            </Button>
          )}
          <Typography variant="body2" color="text.secondary">
            Last updated: {lastUpdated ? new Date(lastUpdated).toLocaleTimeString() : 'Never'}
          </Typography>
        </Box>
      </Box>
      
      {renderFilterSection()}
      
      {renderSummaryCards()}
      
      {/* Active Filters Display */}
      {(selectedUser || selectedCluster || selectedVersion || sortBy !== 'node_id') && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>Active Filters:</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {selectedUser && (
              <Chip 
                label={`User: ${selectedUser}`} 
                onDelete={() => setSelectedUser('')} 
                color="primary" 
                variant="outlined"
              />
            )}
            {selectedCluster && (
              <Chip 
                label={`Cluster: ${selectedCluster}`} 
                onDelete={() => setSelectedCluster('')} 
                color="primary" 
                variant="outlined"
              />
            )}
            {selectedVersion && (
              <Chip 
                label={`Version: ${selectedVersion}`} 
                onDelete={() => setSelectedVersion('')} 
                color="primary" 
                variant="outlined"
              />
            )}
            {sortBy !== 'node_id' && (
              <Chip 
                icon={<SortIcon />}
                label={`Sorted by: ${
                  sortBy === 'last_activity' ? 'Least Recently Used' : 
                  sortBy === 'active_users' ? 'Number of Active Users' : 
                  sortBy === 'version' ? 'Version' : sortBy
                }`} 
                onDelete={() => setSortBy('node_id')} 
                color="secondary" 
                variant="outlined"
              />
            )}
          </Stack>
        </Box>
      )}

      {error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : nodesData.length === 0 && !loading ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No nodes found with the current filter settings. Try adjusting your filters.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {nodesData.map(node => (
            <Grid item xs={12} sm={6} md={4} key={node.node_id}>
              {renderNodeCard(node)}
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default Dashboard;